import { ChangeDetectorRef, Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ALERT_TYPE } from 'src/app/constants';
import { ThumbnailsListComponent } from 'src/app/Images/thumbnails-list/thumbnails-list.component';
import { UploadImageComponent } from 'src/app/Images/upload-image/upload-image.component';
import { ConfirmationDialogComponent } from 'src/app/Shared/confirmation-dialog/confirmation-dialog.component';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { Customer } from 'src/model/customer.model';
import { Docuseal, FormJ } from 'src/model/formJ.model';
import { PaymentSchedule } from 'src/model/paymentSchedule.model';
import { BusinessService } from 'src/services/business.service';
import { FormJService } from 'src/services/form-j.service';
import { GenerateFormjComponent } from '../generate-formj/generate-formj.component';
import { FileService } from 'src/services/file.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { DocusealService } from 'src/services/docuseal.service';

@Component({
	selector: 'app-form-j-history',
	templateUrl: './form-j-history.component.html',
	styleUrls: ['./form-j-history.component.scss'],
	standalone: false
})
export class FormJHistoryComponent implements OnInit {
	private router = inject(Router);
	protected sharedService = inject(SharedService);
	private formJService = inject(FormJService);
	private businessService = inject(BusinessService);
	private docusealService = inject(DocusealService);
	private cdr = inject(ChangeDetectorRef);
	private clipboard = inject(Clipboard);

	@Input() viewCustomer: Customer;
	formJList: FormJ[] = [];
	formJWithFileList: number[] = [];
	formJSigingList: Docuseal[] = [];

	licenseId = signal<number>(undefined);
	hasActiveLicense = computed(() => this.licenseId() !== undefined);

	ngOnInit(): void {
		this.getAllFormJsOfCustomer();
		this.getCustomerFormJsHasFiles();
		this.getCustomerLatestLicense();
		this.getAllFormJSigingDocsOfCustomer();
	}

	openFormJModal() {
		if (this.hasActiveLicense()) {
			const modal = this.sharedService.openModal(GenerateFormjComponent, 'largeModal');
			modal.componentInstance.viewCustomerId = this.viewCustomer.ID;
			modal.componentInstance.licenseId = this.licenseId;
		} else {
			this.sharedService.openAlert('Cannot generate Form J with expired license!', ALERT_TYPE.DANGER);
		}
	}

	private async getAllFormJsOfCustomer() {
		this.formJList = await this.formJService.getCustomerFormJs(this.viewCustomer.ID);
	}

	private async getAllFormJSigingDocsOfCustomer() {
		this.formJSigingList = await this.docusealService.getCustomerFormJDocuseals(this.viewCustomer.ID);
	}

	private async getCustomerFormJsHasFiles() {
		this.formJWithFileList = (await this.formJService.getCustomerFormJsHasFiles(this.viewCustomer.CompanyId, this.viewCustomer.ID)) ?? [];
	}

	private async getCustomerLatestLicense() {
		try {
			const l = await this.businessService.getLatestLicenseByCustomerId(this.viewCustomer.ID);
			this.licenseId.set(l.Id);
		} catch (err) {
			if (err.status === 404) this.licenseId.set(undefined);
		}
	}

	reprintFormJ(formJID: number) {
		this.router.navigateByUrl('/formJ/' + this.viewCustomer.CompanyId + '/' + this.viewCustomer.ID + '/' + formJID);
	}

	processPaymentSchedule(scheduleList: any) {
		const paymentScheduleList: PaymentSchedule[] = [];
		scheduleList.forEach(schedule => {
			const payment = { monthsCount: schedule.monthsCount, amount: schedule.amount };
			paymentScheduleList.push(payment);
		});
		return scheduleList;
	}

	openDeleteFormJDialog(formJ: FormJ) {
		const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
		modalRef.componentInstance.message =
			'Confirm to delete Form J generated on ' + new Date(formJ.DateCreated).toLocaleDateString() + ' with last payment of RM ' + formJ.LastPayment + '?';

		modalRef.result.then(async confirm => {
			if (confirm) {
				await this.formJService.deleteFormJ(formJ.Id);
				window.location.reload();
			}
		});
	}

	openUnstampDialog(formJ: FormJ) {
		const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
		modalRef.componentInstance.message =
			'Confirm to unstamp Form J generated on ' + new Date(formJ.DateCreated).toLocaleDateString() + ' with last payment of RM ' + formJ.LastPayment + '?';

		modalRef.result.then(async confirm => {
			if (confirm) {
				await this.formJService.unstampFormJ(formJ.Id);
				window.location.reload();
			}
		});
	}

	async viewFormJFiles(formJId: number) {
		const files = await this.formJService.getCustomerFormJFiles(this.viewCustomer.ID, formJId);
		const modalRef = this.sharedService.openModal(ThumbnailsListComponent, 'largeModal');
		modalRef.componentInstance.fileArr.set(files);
	}

	uploadStampedFormJ(formJ: FormJ) {
		const modalRef = this.sharedService.openModal(UploadImageComponent);
		modalRef.componentInstance.selectedCustomerId = this.viewCustomer.ID;
		modalRef.componentInstance.isStampedFormJ = true;
		modalRef.componentInstance.formJId = formJ.Id;
		modalRef.componentInstance.formJStampDate = formJ.Date;

		modalRef.componentInstance.passEntry.subscribe((entry: Date) => {
			if (entry) {
				formJ.DateStamped = entry;
				if (this.formJWithFileList.includes(formJ.Id) == false) this.formJWithFileList.push(formJ.Id);
			}
		});
	}

	openSignLink(url: string) {
		this.clipboard.copy(url);
		this.sharedService.openAlert('Copied the link, please send to customer!', ALERT_TYPE.SUCCESS);
	}

	confirmBeforeDeleteDS(formJId: number, index: number) {
		const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
		modalRef.componentInstance.message = 'Confirm to delete signing link ?';

		modalRef.result.then(async confirm => {
			if (confirm) {
				let success = await this.docusealService.deleteFormJDocuseal(formJId);

				if (success) {
					this.formJList[index].DoccusealLink = undefined;
					this.formJList[index].DocusealId = undefined;
					this.cdr.detectChanges();
				}
			}
		});
	}

	async createSignLink(formJId: number, index: number, omitDate: boolean) {
		let success = await this.docusealService.getPostFormJDocuseal(formJId, omitDate);
		if (success) window.location.reload();
	}

	checkIsSigned(docusealId: number) {
		const doc = this.formJSigingList?.find(l => l.id == docusealId);

		return doc?.status == 'completed';
	}

	async downloadSignedDoc(formJId: number) {
		let doc = await this.docusealService.getFormJDocusealDetails(formJId);

		window.open(doc.documentsUrl, '_blank');
	}
}
